'use client';

import * as React from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { TIMEZONE_OPTIONS, getBrowserTimezone } from '@/constants/timezones';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * TimezoneSelector - Comprehensive timezone selector for audience scheduling
 * 
 * Provides worldwide timezone selection using IANA timezone identifiers.
 * Displays offset and region for clarity.
 * 
 * @example
 * ```tsx
 * const [timezone, setTimezone] = useState('America/Chicago');
 * 
 * <TimezoneSelector 
 *   value={timezone}
 *   onChange={setTimezone}
 * />
 * ```
 */

interface TimezoneSelectorProps {
  /**
   * Current timezone value (IANA identifier)
   */
  value: string;
  
  /**
   * Change handler - receives IANA timezone identifier
   */
  onChange: (value: string) => void;
  
  /**
   * Custom label text
   * @default "Timezone"
   */
  label?: string;
  
  /**
   * Whether to show GMT offset in dropdown
   * @default true
   */
  showOffset?: boolean;
  
  /**
   * Whether to show region in dropdown
   * @default true
   */
  showRegion?: boolean;
  
  /**
   * Disabled state
   */
  disabled?: boolean;
  
  /**
   * Additional CSS classes for container
   */
  className?: string;
  
  /**
   * Show browser timezone detection helper
   * @default false
   */
  showBrowserDetection?: boolean;
  /** Keep the label for assistive tech but hide it, when a settings row shows its own. */
  hideLabel?: boolean;
  /** Extra classes for the dropdown panel, e.g. a page's own skin. */
  popoverClassName?: string;
}

export const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({
  value,
  onChange,
  label = 'Timezone',
  showOffset = true,
  showRegion = true,
  disabled = false,
  className = '',
  showBrowserDetection = false,
  hideLabel = false,
  popoverClassName,
}) => {
  const browserTimezone = React.useMemo(() => getBrowserTimezone(), []);
  const isBrowserTimezone = value === browserTimezone;

  const handleUseBrowserTimezone = () => {
    onChange(browserTimezone);
  };

  const formatOptionLabel = (tz: { label: string; offset: string; region?: string }) => {
    const parts: string[] = [];
    
    if (showOffset) {
      parts.push(tz.offset);
    }
    
    parts.push(tz.label);
    
    if (showRegion && tz.region) {
      parts.push(`(${tz.region})`);
    }
    
    return parts.join(' ');
  };

  // Calculate GMT offset for any IANA timezone
  const getOffsetForTimezone = React.useCallback((tz: string): string => {
    try {
      const now = new Date();
      const utcFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const tzFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      
      const utcParts = utcFormatter.formatToParts(now);
      const tzParts = tzFormatter.formatToParts(now);
      
      const utcHours = parseInt(utcParts.find(p => p.type === 'hour')?.value || '0', 10);
      const utcMins = parseInt(utcParts.find(p => p.type === 'minute')?.value || '0', 10);
      const tzHours = parseInt(tzParts.find(p => p.type === 'hour')?.value || '0', 10);
      const tzMins = parseInt(tzParts.find(p => p.type === 'minute')?.value || '0', 10);
      
      // Calculate offset in minutes
      const utcTotalMins = utcHours * 60 + utcMins;
      const tzTotalMins = tzHours * 60 + tzMins;
      let offsetMins = tzTotalMins - utcTotalMins;
      
      // Handle day boundary (could be +/- 24 hours)
      if (offsetMins > 720) offsetMins -= 1440;
      if (offsetMins < -720) offsetMins += 1440;
      
      const offsetHours = Math.floor(offsetMins / 60);
      const offsetMinutes = Math.abs(offsetMins % 60);
      const sign = offsetHours >= 0 ? '+' : '-';
      
      return `(GMT${sign}${Math.abs(offsetHours).toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')})`;
    } catch {
      return '(GMT+00:00)';
    }
  }, []);

  // Get all IANA timezones (comprehensive list like react-timezone-select)
  const getAllIANATimezones = React.useMemo(() => {
    try {
      // Use Intl.supportedValuesOf to get all IANA timezones (modern browsers)
      if (typeof Intl !== 'undefined' && 'supportedValuesOf' in Intl) {
        const allIANA = (Intl as any).supportedValuesOf('timeZone') as string[];
        return allIANA.map(tz => ({
          value: tz,
          label: tz.split('/').pop()?.replace(/_/g, ' ') || tz,
          offset: getOffsetForTimezone(tz),
          region: tz.split('/')[0] || 'Other',
        }));
      }
      
      // Fallback: Use curated list + common timezones
      const commonTimezones = [
        'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
        'America/Toronto', 'America/Vancouver', 'America/Mexico_City',
        'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome', 'Europe/Madrid',
        'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Hong_Kong', 'Asia/Singapore', 'Asia/Dubai',
        'Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland',
      ];
      
      const allTzSet = new Set([...TIMEZONE_OPTIONS.map(t => t.value), ...commonTimezones]);
      if (!allTzSet.has(browserTimezone)) {
        allTzSet.add(browserTimezone);
      }
      
      return Array.from(allTzSet).map(tz => {
        const existing = TIMEZONE_OPTIONS.find(t => t.value === tz);
        if (existing) return existing;
        
        return {
          value: tz,
          label: tz.split('/').pop()?.replace(/_/g, ' ') || tz,
          offset: getOffsetForTimezone(tz),
          region: tz.split('/')[0] || 'Other',
        };
      }).sort((a, b) => {
        // Sort by offset, then by label
        const offsetA = a.offset;
        const offsetB = b.offset;
        if (offsetA !== offsetB) return offsetA.localeCompare(offsetB);
        return a.label.localeCompare(b.label);
      });
    } catch {
      // Ultimate fallback: use curated list
      const browserTzExists = TIMEZONE_OPTIONS.some(tz => tz.value === browserTimezone);
      if (browserTzExists) {
        return TIMEZONE_OPTIONS;
      }
      
      return [
        ...TIMEZONE_OPTIONS,
        {
          value: browserTimezone,
          label: browserTimezone.split('/').pop()?.replace('_', ' ') || browserTimezone,
          offset: getOffsetForTimezone(browserTimezone),
          region: 'Browser',
        },
      ];
    }
  }, [browserTimezone, getOffsetForTimezone]);

  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  // Filter timezones based on search
  const filteredTimezones = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return getAllIANATimezones;
    }
    
    const query = searchQuery.toLowerCase();
    return getAllIANATimezones.filter(tz =>
      tz.value.toLowerCase().includes(query) ||
      tz.label.toLowerCase().includes(query) ||
      tz.region?.toLowerCase().includes(query)
    );
  }, [getAllIANATimezones, searchQuery]);

  // Get current timezone display info
  const currentTimezone = React.useMemo(() => {
    return getAllIANATimezones.find(tz => tz.value === value) || {
      value,
      label: value.split('/').pop()?.replace(/_/g, ' ') || value,
      offset: getOffsetForTimezone(value),
      region: value.split('/')[0] || 'Other',
    };
  }, [value, getAllIANATimezones, getOffsetForTimezone]);

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <label htmlFor="timezone" className={cn("text-sm font-medium text-slate-900", hideLabel && "sr-only")}>
          {label}
        </label>
        {showBrowserDetection && !isBrowserTimezone && (
          <Button
            type="button"
            variant='ghost'
            size='sm'
            onClick={handleUseBrowserTimezone}
            className="text-xs text-mb-blue-600 hover:text-mb-blue-700 underline h-auto p-0"
          >
            Use my timezone
          </Button>
        )}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between"
            id="timezone"
          >
            <span className="truncate">
              {formatOptionLabel(currentTimezone)}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className={cn("w-full p-0", popoverClassName)} align="start">
          <Command>
            <CommandInput 
              placeholder="Search timezone..." 
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              <CommandEmpty>No timezone found.</CommandEmpty>
              <CommandGroup>
                {filteredTimezones.map((tz) => (
                  <CommandItem
                    key={tz.value}
                    value={tz.value}
                    onSelect={() => {
                      onChange(tz.value);
                      setOpen(false);
                      setSearchQuery('');
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === tz.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {formatOptionLabel(tz)}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {showBrowserDetection && isBrowserTimezone && (
        <p className="text-xs text-slate-500">
          Using your browser timezone
        </p>
      )}
    </div>
  );
};
